import React from 'react';
import { cn } from '../../lib/utils';
import { money } from './helpers';
import { Eye, EyeOff, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useNavigation } from 'react-day-picker';

export function Group({ title, children, footer, dense }) {
  return (
    <div>
      <div className="text-[14px] font-bold text-neutral-900 dark:text-neutral-100 mb-4 pb-2 border-b hairline">{title}</div>
      <div className={cn('grid gap-5',
        dense ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      )}>
        {children}
      </div>
      {footer}
    </div>
  );
}

// ---- input mask helpers (date MM/DD/YYYY, SSN XXX-XX-XXXX) ----
function maskDate(raw) {
  const d = String(raw ?? '').replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}
function maskSsn(raw) {
  const d = String(raw ?? '').replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}
// Phone mask — accepts pasted "(480) 221-8167", "+1 480-221-8167", "4802218167",
// strips everything down to 10 digits, renders as 480-221-8167.
function maskPhone(raw) {
  let d = String(raw ?? '').replace(/\D/g, '');
  // Drop a leading "1" (US country code) if present so the user gets 10 digits.
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}
// Sanitize numeric input: keep digits, one dot, one leading minus.
// Strip leading zeros so "05" → "5" but preserve "0.42".
function sanitizeNumeric(raw, allowDecimal = true) {
  let s = String(raw ?? '');
  if (allowDecimal) {
    // allow: digits, single dot, single leading minus
    s = s.replace(/[^\d.\-]/g, '');
    // keep only the first dot
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    // keep only leading minus
    s = s.replace(/(?!^)-/g, '');
  } else {
    s = s.replace(/[^\d\-]/g, '').replace(/(?!^)-/g, '');
  }
  // strip leading zeros: "007" → "7", "-007" → "-7"; preserve "0." and "-0."
  s = s.replace(/^(-?)0+(?=\d)/, '$1');
  return s;
}

export function Field({ label, value, onChange, type = 'text', placeholder, prefix, suffix, required, mono, colSpan = 1, step, mask, testId, secret = false }) {
  const isNumber = type === 'number';
  const isMasked = mask === 'date' || mask === 'ssn' || mask === 'phone';

  // Internal draft for numeric fields so that mid-typing strings like "6."
  // (or empty) survive the round-trip through Number() in the parent setter.
  const [draft, setDraft] = React.useState(null);
  // For sensitive fields (e.g. SSN): start hidden, the eye toggle reveals.
  const [revealed, setRevealed] = React.useState(false);
  // Track focus so we don't obfuscate / lock the input while the user is
  // actively typing. Obfuscation kicks in once they blur the field.
  const [focused, setFocused] = React.useState(false);
  // Only obfuscate / lock when the user already has a value to protect AND
  // is not currently editing it.
  const hasValue = secret && value != null && String(value).replace(/\D/g, '').length >= 5;
  const isHidden = secret && !revealed && hasValue && !focused;

  // Mask a finished SSN like "625-52-0195" → "xxx-xx-0195" so the LO can
  // still verify the last 4 without exposing the full number. Empty or
  // mid-typed values render normally so the user knows what they're typing.
  const obfuscateSsn = (formatted) => {
    const s = String(formatted ?? '');
    if (!s) return '';
    const digits = s.replace(/\D/g, '');
    if (digits.length < 5) return s; // let the user type the first 5 in the clear
    const last4 = digits.slice(-4);
    return `xxx-xx-${last4.padStart(4, '0')}`;
  };

  // Dollar-prefixed integer fields get pretty comma formatting when not focused
  // and the clean Bricolage Grotesque "display" font instead of the JetBrains
  // mono treatment. Editing falls back to raw digits so caret behavior stays
  // sane (typing "1234567" feels normal — we re-format on blur).
  const isDollar = isNumber && prefix === '$';

  // Determine display value (controlled).
  const display = (() => {
    if (mask === 'date') return maskDate(value);
    if (mask === 'ssn') {
      const formatted = maskSsn(value);
      return isHidden ? obfuscateSsn(formatted) : formatted;
    }
    if (mask === 'phone') return maskPhone(value);
    if (isNumber) {
      // If user is actively editing AND the draft parses to the parent's value,
      // prefer the draft so trailing "." or "" stays put.
      if (draft != null) {
        const parsed = draft === '' || draft === '-' || draft === '.' || draft === '-.' ? 0 : Number(draft);
        if (!Number.isNaN(parsed) && parsed === Number(value || 0)) return draft;
      }
      if (value === 0 || value === '0' || value == null || value === '' || Number.isNaN(value)) return '';
      // Pretty-format $ amounts with commas when the user is not typing in
      // the field. Whole-dollar amounts get no decimals; fractional cents
      // (e.g. 1234.5) keep up to 2 decimals.
      if (isDollar && !focused) {
        const n = Number(value);
        if (!Number.isFinite(n)) return String(value);
        return Number.isInteger(n)
          ? n.toLocaleString('en-US')
          : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
      }
      return String(value);
    }
    return value ?? '';
  })();

  const handleChange = (raw) => {
    if (mask === 'date') { setDraft(null); return onChange(maskDate(raw)); }
    if (mask === 'ssn')  {
      setDraft(null);
      // If the field is currently obfuscated, ignore the synthetic edit
      // (otherwise typing into "xxx-xx-0195" would corrupt the real value).
      // The user must click the eye to reveal before editing.
      if (isHidden) return;
      return onChange(maskSsn(raw));
    }
    if (mask === 'phone') { setDraft(null); return onChange(maskPhone(raw)); }
    if (isNumber) {
      const cleaned = sanitizeNumeric(raw, true);
      setDraft(cleaned);
      return onChange(cleaned);
    }
    setDraft(null);
    onChange(raw);
  };

  // Render as type="text" for number/masked so we own the formatting.
  const inputType = isNumber || isMasked ? 'text' : type;
  const inputMode = isNumber ? 'decimal' : (mask === 'phone' ? 'tel' : (mask ? 'numeric' : undefined));
  const maxLen = mask === 'date' ? 10 : mask === 'ssn' ? 11 : mask === 'phone' ? 12 : undefined;
  // For SSN we always reserve right padding for the eye button so it never
  // overlaps the value (px-10 ≈ 40px).
  const rightPad = secret || suffix ? 'pr-10' : 'pr-3';

  return (
    <div className={cn(colSpan === 2 && 'md:col-span-2', colSpan === 3 && 'md:col-span-3', colSpan === 4 && 'md:col-span-4 lg:col-span-4')}>
      <label className="text-[12.5px] font-semibold text-neutral-700 dark:text-neutral-300 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5 relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-neutral-500 dark:text-neutral-400 font-semibold display">
            {prefix}
          </span>
        )}
        <input
          type={inputType}
          inputMode={inputMode}
          step={step}
          maxLength={maxLen}
          data-testid={testId}
          value={display}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            if (isNumber) setDraft(null);
          }}
          placeholder={placeholder}
          readOnly={isHidden}
          autoComplete={secret ? 'off' : undefined}
          className={cn(
            'w-full h-11 rounded-md bg-white dark:bg-neutral-900 border hairline text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 placeholder:font-normal placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:border-neutral-900 dark:focus:border-[hsl(var(--kf-gold))] transition',
            prefix ? 'pl-7' : 'pl-3',
            rightPad,
            // Numbers, masked values (date/ssn/phone), and any explicit `mono`
            // request all share the Bricolage Grotesque "display" font for a
            // consistent, designed feel across the form.
            (isNumber || isMasked || mono) && 'display tracking-tight'
          )}
        />
        {secret && (
          <button
            type="button"
            data-testid={testId ? `${testId}-reveal` : 'field-reveal'}
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Hide value' : 'Show value'}
            title={revealed ? 'Hide' : 'Reveal'}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center transition"
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        {suffix && !secret && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12.5px] text-neutral-400 dark:text-neutral-500 display">{suffix}</span>}
      </div>
    </div>
  );
}

// Parses MM/DD/YYYY → Date, returns null if invalid.
function parseDateStr(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [_, mm, dd, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(d.getTime()) ? null : d;
}
function formatDateStr(d) {
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

/**
 * DateField — Masked MM/DD/YYYY text input + calendar icon trigger that
 * opens a Popover with a Calendar for point-and-click selection.
 * Value is stored as the MM/DD/YYYY string, identical to mask="date".
 */
const MONTH_LABELS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function CalendarCaption({ displayMonth }) {
  const { goToMonth, previousMonth, nextMonth } = useNavigation();
  const [mode, setMode] = React.useState('none'); // 'none' | 'month' | 'year'
  const yearListRef = React.useRef(null);
  const currentYear = displayMonth.getFullYear();
  const currentMonth = displayMonth.getMonth();
  const thisYear = new Date().getFullYear();
  const years = React.useMemo(
    () => Array.from({ length: thisYear - 1940 + 2 }, (_, i) => 1940 + i),
    [thisYear]
  );

  // When the year list opens, scroll the active year into view.
  React.useEffect(() => {
    if (mode === 'year' && yearListRef.current) {
      const el = yearListRef.current.querySelector(`[data-year="${currentYear}"]`);
      if (el) el.scrollIntoView({ block: 'center' });
    }
  }, [mode, currentYear]);

  const setMonth = (mi) => {
    const d = new Date(currentYear, mi, 1);
    goToMonth(d);
    setMode('none');
  };
  const setYear = (y) => {
    const d = new Date(y, currentMonth, 1);
    goToMonth(d);
    setMode('none');
  };

  return (
    <div className="relative flex items-center justify-between px-1 pb-2">
      <button
        type="button"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className="h-7 w-7 rounded-md hover:bg-neutral-100 disabled:opacity-30 flex items-center justify-center text-neutral-700"
        aria-label="Previous month"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-1 text-[13px] font-bold text-neutral-900">
        <button
          type="button"
          data-testid="cal-caption-month"
          onClick={() => setMode((m) => (m === 'month' ? 'none' : 'month'))}
          className="px-2 py-1 rounded-md hover:bg-neutral-100 transition"
        >
          {MONTH_LABELS[currentMonth]}
        </button>
        <button
          type="button"
          data-testid="cal-caption-year"
          onClick={() => setMode((m) => (m === 'year' ? 'none' : 'year'))}
          className="px-2 py-1 rounded-md hover:bg-neutral-100 transition mono"
        >
          {currentYear}
        </button>
      </div>
      <button
        type="button"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className="h-7 w-7 rounded-md hover:bg-neutral-100 disabled:opacity-30 flex items-center justify-center text-neutral-700"
        aria-label="Next month"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {mode === 'month' && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border hairline bg-white shadow-lg p-2 grid grid-cols-3 gap-1">
          {MONTH_LABELS.map((m, i) => (
            <button
              key={m}
              type="button"
              data-testid={`cal-month-${m.toLowerCase()}`}
              onClick={() => setMonth(i)}
              className={cn(
                'px-2 py-1.5 rounded-md text-[12px] font-semibold transition',
                i === currentMonth
                  ? 'bg-neutral-900 text-white'
                  : 'text-neutral-700 hover:bg-neutral-100'
              )}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
      )}

      {mode === 'year' && (
        <div
          ref={yearListRef}
          className="absolute left-1/2 -translate-x-1/2 top-full z-20 mt-1 w-32 max-h-56 overflow-y-auto rounded-lg border hairline bg-white shadow-lg p-1"
        >
          {years.map((y) => (
            <button
              key={y}
              type="button"
              data-year={y}
              data-testid={`cal-year-${y}`}
              onClick={() => setYear(y)}
              className={cn(
                'w-full text-center px-2 py-1.5 rounded-md text-[12.5px] font-semibold mono transition',
                y === currentYear
                  ? 'bg-neutral-900 text-white'
                  : 'text-neutral-700 hover:bg-neutral-100'
              )}
            >
              {y}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function DateField({ label, value, onChange, placeholder = 'MM/DD/YYYY', required, colSpan = 1, testId }) {
  const [open, setOpen] = React.useState(false);
  const selected = parseDateStr(value);
  return (
    <div className={cn(colSpan === 2 && 'md:col-span-2')}>
      <label className="text-[12.5px] font-semibold text-neutral-700 dark:text-neutral-300 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5 relative">
        <input
          type="text"
          inputMode="numeric"
          maxLength={10}
          data-testid={testId}
          value={maskDate(value)}
          onChange={(e) => onChange(maskDate(e.target.value))}
          placeholder={placeholder}
          className="w-full h-11 rounded-md bg-white dark:bg-neutral-900 border hairline text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 placeholder:font-normal placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:border-neutral-900 dark:focus:border-[hsl(var(--kf-gold))] transition pl-3 pr-10 display tracking-tight"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={testId ? `${testId}-calendar` : undefined}
              aria-label={`Pick ${label || 'date'}`}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center transition"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="p-0 w-auto" sideOffset={4}>
            <Calendar
              mode="single"
              selected={selected || undefined}
              defaultMonth={selected || new Date()}
              components={{ Caption: CalendarCaption }}
              onSelect={(d) => {
                onChange(formatDateStr(d));
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}



export function Select({ label, value, onChange, options, colSpan = 1, placeholder = 'Select…' }) {
  const opts = options.map((o) => typeof o === 'string' ? { v: o, l: o } : o);
  // If the current value isn't in the canonical option list (e.g. legacy
  // data like "Multi-Family" left over from seed rows), surface it as a
  // disabled red option so the LO can see "this value is non-standard"
  // instead of the native <select> silently rendering blank.
  const isOrphan = !!value && !opts.some((o) => o.v === value);
  return (
    <div className={cn(colSpan === 2 && 'md:col-span-2', colSpan === 3 && 'md:col-span-3', colSpan === 4 && 'md:col-span-4')}>
      <label className="text-[12.5px] font-semibold text-neutral-700 dark:text-neutral-300">{label}</label>
      <div className="mt-1.5">
        <select value={value || ''} onChange={(e) => onChange(e.target.value)}
          className={cn(
            "w-full h-11 px-3 pr-8 rounded-md bg-white dark:bg-neutral-900 border hairline text-[14px] font-semibold focus:outline-none focus:border-neutral-900 dark:focus:border-[hsl(var(--kf-gold))] transition appearance-none bg-no-repeat bg-[right_0.7rem_center] bg-[length:0.85em] bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23737373%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22/%3E%3C/svg%3E')] dark:bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23a3a3a3%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22/%3E%3C/svg%3E')]",
            isOrphan ? 'text-red-600 border-red-300' : (value ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400 dark:text-neutral-500')
          )}>
          <option value="" disabled hidden>{placeholder}</option>
          {isOrphan && (
            <option key={`orphan-${value}`} value={value}>{`⚠ ${value} (legacy — pick a valid option)`}</option>
          )}
          {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
    </div>
  );
}

export function Toggle({ active, label, onClick }) {
  return (
    <button onClick={onClick}
      className={cn('h-8 px-3 rounded-md text-[11.5px] font-bold border transition',
        active
          ? 'bg-[hsl(var(--kf-gold))] text-neutral-900 border-[hsl(var(--kf-gold-deep))]/40 ring-1 ring-[hsl(var(--kf-gold-deep))]/30'
          : 'bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 hairline hover:border-[hsl(var(--kf-gold))] hover:text-[hsl(var(--kf-gold-deep))]')}>
      {label}
    </button>
  );
}

export function Stat({ label, value, mono, accent }) {
  return (
    <div className={cn('rounded-xl border bg-white dark:bg-neutral-900 p-4 card-lift', accent ? 'border-[hsl(var(--kf-gold))]/40' : 'hairline')}>
      <div className="text-[11.5px] font-semibold text-neutral-500 dark:text-neutral-400">{label}</div>
      <div className={cn('display text-[22px] font-bold tracking-tight mt-1',
        accent && 'text-[hsl(var(--kf-gold-deep))]',
        mono && 'mono',
        !accent && 'text-neutral-900 dark:text-neutral-100')}>{value}</div>
    </div>
  );
}

export function PayRow({ label, value, total, color }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-neutral-700 dark:text-neutral-300">
        <span>{label}</span>
        <span className="mono text-neutral-900 dark:text-neutral-100 font-bold">{money(value)}</span>
      </div>
      <div className="h-1 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden mt-1">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
